import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Bell, Check, Trash2, Plus, BellOff } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useLocalStorageState } from "../lib/useLocalStorageState";
import { createAudioContext, startAlarmLoop } from "../lib/alarmAudio";

/**
 * TIME Watch → Utility → Promemoria
 * Promemoria classico e minimale.
 * - Creazione: testo + data + ora
 * - Elenco con stato attivo/completato
 * - Elimina + segna come completato
 * - Alla scadenza: notifica visiva + suono semplice
 *
 * Persistenza via localStorage (chiave "time-watch:reminders"), coerente
 * con il resto del progetto.
 */

const STORAGE_KEY = "time-watch:reminders";

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const toDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
};

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Pattern sonoro Promemoria: beep beep — beep beep — beep beep.
// La sequenza è avviata quando c'è almeno un promemoria "fired" attivo e
// viene interrotta al click su "Interrompi".

export default function TimeWatchPromemoriaPage() {
  const [reminders, setReminders] = useLocalStorageState(STORAGE_KEY, []);
  const [text, setText] = useState("");
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState("18:00");
  const [firedIds, setFiredIds] = useState([]); // notifica visiva attiva

  const audioCtxRef = useRef(null);
  const stopLoopRef = useRef(null);
  const alreadyFiredRef = useRef(new Set()); // evita doppie riproduzioni

  const addReminder = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !date || !time) return;
    const newReminder = {
      id: uid(),
      text: trimmed,
      date,
      time,
      completed: false,
      createdAt: new Date().toISOString(),
    };
    setReminders((prev) => [newReminder, ...prev]);
    setText("");
    // Richiedi il permesso notifiche in modo trasparente (una sola volta).
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().catch(() => {});
    }
  };

  const toggleCompleted = (id) => {
    setReminders((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, completed: !r.completed } : r
      )
    );
  };

  const removeReminder = (id) => {
    setReminders((prev) => prev.filter((r) => r.id !== id));
    setFiredIds((prev) => prev.filter((x) => x !== id));
    alreadyFiredRef.current.delete(id);
  };

  // Interrompi TUTTI i promemoria scaduti attivi in una volta sola:
  // ferma il suono e chiude i popup, marcando tutti come completati.
  const interrompiAll = () => {
    if (stopLoopRef.current) {
      stopLoopRef.current();
      stopLoopRef.current = null;
    }
    const ids = firedIds.slice();
    setReminders((prev) =>
      prev.map((r) => (ids.includes(r.id) ? { ...r, completed: true } : r))
    );
    setFiredIds([]);
  };

  // Ordinamento: attivi prima, poi per data/ora crescente.
  const sortedReminders = useMemo(() => {
    return [...reminders].sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const aKey = `${a.date}T${a.time}`;
      const bKey = `${b.date}T${b.time}`;
      return aKey.localeCompare(bKey);
    });
  }, [reminders]);

  // Polling ogni 15s per verificare le scadenze (sufficientemente reattivo,
  // senza sovraccaricare). Attiva la notifica visiva la prima volta.
  // setInterval funziona anche con tab in background (throttled), quindi
  // al rientro in foreground i promemoria scaduti sono già in coda.
  useEffect(() => {
    const check = () => {
      const now = Date.now();
      const dueIds = [];
      const dueList = [];
      reminders.forEach((r) => {
        if (r.completed) return;
        if (alreadyFiredRef.current.has(r.id)) return;
        const dueAt = new Date(`${r.date}T${r.time}:00`).getTime();
        if (!Number.isFinite(dueAt)) return;
        if (dueAt <= now) {
          dueIds.push(r.id);
          dueList.push(r);
          alreadyFiredRef.current.add(r.id);
        }
      });
      if (dueIds.length > 0) {
        setFiredIds((prev) => [...prev, ...dueIds]);
        // notifica di sistema (una per promemoria) se tab non visibile
        maybeShowSystemNotifications(dueList);
      }
    };
    check();
    const iv = setInterval(check, 15000);
    // Rientro in foreground: forza un check immediato per coprire il gap.
    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reminders]);

  // Avvia/ferma il loop sonoro in base alla presenza di popup attivi.
  useEffect(() => {
    if (firedIds.length > 0 && !stopLoopRef.current) {
      if (!audioCtxRef.current) audioCtxRef.current = createAudioContext();
      // Promemoria → 2 beep per ciclo (aggiornamento Sprint).
      stopLoopRef.current = startAlarmLoop(audioCtxRef.current, [2]);
    } else if (firedIds.length === 0 && stopLoopRef.current) {
      stopLoopRef.current();
      stopLoopRef.current = null;
    }
    return () => {
      // cleanup su unmount
      if (firedIds.length === 0 && stopLoopRef.current) {
        stopLoopRef.current();
        stopLoopRef.current = null;
      }
    };
  }, [firedIds.length]);

  const maybeShowSystemNotifications = (list) => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted" ||
      document.visibilityState === "visible"
    )
      return;
    list.forEach((r) => {
      try {
        new Notification("Promemoria", {
          body: r.text,
          tag: `promemoria-${r.id}`,
          renotify: true,
        });
      } catch {
        /* ignore */
      }
    });
  };

  const firedReminders = reminders.filter((r) => firedIds.includes(r.id));

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-3xl mx-auto">
      <Link
        to="/innovazione/time-watch/utility"
        data-testid="time-watch-promemoria-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Utility
      </Link>

      <PageHeader
        eyebrow="Utility"
        title="Promemoria"
        description="Scrivi un promemoria, scegli data e ora."
        testId="time-watch-promemoria-header"
      />

      {/* Popup allarme — mostra tutti i promemoria scaduti attivi.
          Interrompi ferma il suono, chiude il popup e marca come completati. */}
      {firedReminders.length > 0 && (
        <PromemoriaFiredOverlay
          reminders={firedReminders}
          onInterrompi={interrompiAll}
        />
      )}

      {/* Form */}
      <form
        onSubmit={addReminder}
        data-testid="promemoria-form"
        className="glass-card p-6 sm:p-8 mb-10 space-y-4"
      >
        <div>
          <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">
            Testo
          </label>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Es. Chiamare Fabio"
            data-testid="promemoria-input-text"
            className="w-full bg-transparent border-b border-white/15 focus:border-emerald-400/60 outline-none text-white font-light text-lg py-2 placeholder:text-white/25"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">
              Data
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="promemoria-input-date"
              className="w-full bg-transparent border-b border-white/15 focus:border-emerald-400/60 outline-none text-white font-light py-2 [color-scheme:dark]"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">
              Ora
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="promemoria-input-time"
              className="w-full bg-transparent border-b border-white/15 focus:border-emerald-400/60 outline-none text-white font-light py-2 [color-scheme:dark]"
            />
          </div>
        </div>
        <div className="pt-2">
          <button
            type="submit"
            disabled={!text.trim() || !date || !time}
            data-testid="promemoria-save-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Salva promemoria
          </button>
        </div>
      </form>

      {/* Elenco */}
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-4">
          Elenco ({reminders.length})
        </div>
        {sortedReminders.length === 0 ? (
          <div
            data-testid="promemoria-empty"
            className="text-white/40 font-light py-8 text-center"
          >
            Nessun promemoria. Aggiungine uno dal modulo qui sopra.
          </div>
        ) : (
          <ul
            data-testid="promemoria-list"
            className="space-y-3"
          >
            {sortedReminders.map((r) => (
              <li
                key={r.id}
                data-testid={`promemoria-item-${r.id}`}
                className={`glass-card p-4 sm:p-5 flex items-center gap-4 transition ${
                  r.completed ? "opacity-50" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleCompleted(r.id)}
                  data-testid={`promemoria-toggle-${r.id}`}
                  aria-label={
                    r.completed
                      ? "Segna come attivo"
                      : "Segna come completato"
                  }
                  className={`w-6 h-6 rounded-full border flex items-center justify-center transition ${
                    r.completed
                      ? "bg-emerald-500/80 border-emerald-400 text-black"
                      : "border-white/25 hover:border-emerald-400/60"
                  }`}
                >
                  {r.completed && <Check className="w-3.5 h-3.5" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-white font-light truncate ${
                      r.completed ? "line-through" : ""
                    }`}
                  >
                    {r.text}
                  </div>
                  <div className="text-[11px] text-white/50 mt-1">
                    {toDisplayDate(r.date)} · {r.time} ·{" "}
                    <span
                      className={
                        r.completed
                          ? "text-white/40"
                          : "text-emerald-300/80"
                      }
                    >
                      {r.completed ? "Completato" : "Attivo"}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeReminder(r.id)}
                  data-testid={`promemoria-delete-${r.id}`}
                  aria-label="Elimina promemoria"
                  className="p-2 rounded-full text-white/40 hover:text-red-300 hover:bg-white/5 transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PromemoriaFiredOverlay({ reminders, onInterrompi }) {
  return (
    <div
      data-testid="promemoria-fired-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-6"
    >
      <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-300 mb-6 animate-pulse flex items-center gap-2">
        <Bell className="w-4 h-4" />
        Promemoria
      </div>
      <div
        data-testid="promemoria-fired-list"
        className="w-full max-w-md space-y-3 max-h-[45vh] overflow-y-auto"
      >
        {reminders.map((r) => (
          <div
            key={r.id}
            data-testid={`promemoria-fired-${r.id}`}
            className="glass-card p-5 border-emerald-400/50 shadow-[0_0_40px_-10px_rgba(16,185,129,0.5)]"
          >
            <div className="text-white font-light text-lg leading-snug">
              {r.text}
            </div>
            <div className="text-[11px] text-white/50 mt-2">
              {r.date.split("-").reverse().join("/")} · {r.time}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onInterrompi}
        data-testid="promemoria-interrompi-button"
        autoFocus
        className="mt-12 inline-flex items-center gap-2 px-10 py-4 rounded-full bg-red-500/90 text-white text-sm tracking-wide font-medium hover:bg-red-400 transition"
      >
        <BellOff className="w-4 h-4" />
        Interrompi
      </button>
    </div>
  );
}
